<template>
  <b-modal dialog-class="modal-lg"
           :title="helper.trans('labels.referral_store_fees')"
           :modelValue="modal"
           @update:modelValue="modal = $event"
  >
    <template #default>
      <div style="min-height: 200px">
        <div v-for="error in generalErrors" :key="error.message" class="alert alert-danger mt-2 mb-0">
          {{ error.message }}
        </div>

        <div class="row">
          <div class="col-sm-4 referral-col small text-muted text-capitalize-first">{{ helper.trans('labels.referral') }}</div>
          <div class="col-sm-4 referral-col small text-muted text-capitalize-first">{{ helper.trans('labels.pay_in_reward_fee_percent') }}</div>
          <div class="col-sm-4 referral-col small text-muted text-capitalize-first">{{ helper.trans('labels.pay_out_reward_fee_percent') }}</div>
        </div>
        <div class="row mb-3 align-items-start" v-for="row in rows" :key="row.slot">
          <div class="col-sm-4 referral-col">
            <FormInputWrapper v-slot="parentProps" :errors="errorsFor(row.slot, 'referralId')">
              <Multiselect
                  :class="parentProps.class"
                  :model-value="row.referralUser"
                  track-by="id"
                  :options="referrals"
                  :custom-label="(opt: any) => `${opt.name} (${opt.email})`"
                  select-label="Select"
                  deselect-label="✕"
                  :placeholder="helper.trans('labels.referral_store_override_placeholder')"
                  open-direction="bottom"
                  :multiple="false"
                  :searchable="true"
                  :internal-search="true"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :options-limit="100"
                  :limit="1000"
                  :show-no-results="false"
                  :hide-selected="false"
                  @search-change="asyncFindReferrals"
                  @select="(user: any) => onSelectReferral(row, user)"
                  @remove="() => onRemoveReferral(row)"
                  :loading="isReferralsSelectLoading"
              >
              </Multiselect>
            </FormInputWrapper>
          </div>
          <div class="col-sm-4 referral-col">
            <FormInputWrapper v-slot="parentProps" :errors="errorsFor(row.slot, 'payInFee')">
              <div class="input-group input-group-sm" :class="parentProps.class">
                <input v-model="row.payInFee" type="text" class="form-control"
                       v-maska
                       data-maska="99.99"
                       data-maska-tokens="9:[0-9]:optional"
                       :placeholder="String(row.merchantPayInFee)"
                >
                <button @click.prevent="toggleReferralFeeType(row, 'payIn')" class="btn btn-outline-secondary" type="button">
                  {{ helper.trans(`labels.ref_fee_type.${row.payInFeeType}`) }}
                </button>
              </div>
            </FormInputWrapper>
          </div>
          <div class="col-sm-4 referral-col">
            <FormInputWrapper v-slot="parentProps" :errors="errorsFor(row.slot, 'payOutFee')">
              <div class="input-group input-group-sm" :class="parentProps.class">
                <input v-model="row.payOutFee" type="text" class="form-control"
                       v-maska
                       data-maska="99.99"
                       data-maska-tokens="9:[0-9]:optional"
                       :placeholder="String(row.merchantPayOutFee)"
                >
                <button @click.prevent="toggleReferralFeeType(row, 'payOut')" class="btn btn-outline-secondary" type="button">
                  {{ helper.trans(`labels.ref_fee_type.${row.payOutFeeType}`) }}
                </button>
              </div>
            </FormInputWrapper>
          </div>
        </div>

        <small class="d-block text-muted mt-3">{{ helper.trans('labels.referral_store_fees_hint') }}</small>
      </div>
    </template>
    <template #footer>
      <button type="button" @click="modal = false" class="btn btn-outline-secondary btn-sm text-capitalize-first">
        {{ helper.trans('button.cancel') }}
      </button>
      <button type="button" @click="apply()" class="btn btn-outline-primary btn-sm text-capitalize-first">
        {{ helper.trans('button.apply') }}
      </button>
    </template>
  </b-modal>
</template>

<style scoped>
/* глобальный .row{--bs-gutter-x:0} в styles.css убивает штатный отступ колонок бутстрапа — расставляем зазор явным padding'ом */
.referral-col + .referral-col {
  padding-left: 24px;
}

:deep(.multiselect__single) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

:deep(.multiselect__tags) {
  min-height: 44px;
}
</style>

<script lang="ts">
import {defineComponent, reactive, toRefs} from 'vue'
import {BModal} from "bootstrap-vue-3";
import Multiselect from "vue-multiselect";
import {helper} from "@/service/CommonHelper";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";
import {ReferralStoreSettingRowFormDTO} from "@/models/form/MerchantStoreFormDTO";
import {referralSlotKey} from "@/models/ReferralStoreSettingDTO";
import UserDTO from "@/models/UserDTO";
import {UserRole} from "@/models/enum/UserRole";
import PaginationDTO from "@/models/PaginationDTO";
import {api} from "@/service/APIClient";

export default defineComponent({
  components: {
    BModal,
    Multiselect,
    FormInputWrapper,
  },
  emits: ['apply'],
  setup(props, {emit}) {
    const state = reactive({
      modal: false,
      rows: [] as ReferralStoreSettingRowFormDTO[],
      rowErrors: {} as Record<number, ValidationErrorDTO[]>,
      generalErrors: [] as ValidationErrorDTO[],
      referrals: [] as UserDTO[],
      isReferralsSelectLoading: false,
    })

    const asyncFindReferrals = async (query: string) => {
      state.isReferralsSelectLoading = true
      state.referrals = (await api.users({limit: 100, offset: 0} as PaginationDTO, {query: query, roles: [UserRole.REFERRAL]})).items
      state.isReferralsSelectLoading = false
    }

    // рефералы, назначенные на сторе, но не попавшие в дефолтную выборку asyncFindReferrals('') — догружаем по id для отображения имени в пикере
    const hydrateReferralOverrides = async () => {
      const missingIds = state.rows
        .filter((row) => row.referralId !== null && row.referralUser === null)
        .map((row) => row.referralId as number)

      if (missingIds.length === 0) {
        return
      }

      const resp = await api.users({limit: missingIds.length, offset: 0} as PaginationDTO, {ids: missingIds})
      for (const user of resp.items) {
        for (const row of state.rows) {
          if (row.referralId === user.id) {
            row.referralUser = user
          }
        }
      }
    }

    const onSelectReferral = (row: ReferralStoreSettingRowFormDTO, user: UserDTO) => {
      row.referralUser = user
      row.referralId = user.id
    }

    const onRemoveReferral = (row: ReferralStoreSettingRowFormDTO) => {
      row.referralUser = null
      row.referralId = null
    }

    const toggleReferralFeeType = (row: ReferralStoreSettingRowFormDTO, direction: 'payIn' | 'payOut') => {
      if (direction === 'payIn') {
        row.payInFeeType = row.payInFeeType === 0 ? 1 : 0

        return
      }

      row.payOutFeeType = row.payOutFeeType === 0 ? 1 : 0
    }

    const errorsFor = (slot: number, field: string): ValidationErrorDTO[] =>
      (state.rowErrors[slot] ?? []).filter((error) => error.field === field)

    // ошибки бэка: referralSettings | referralSettings.referral1 | referralSettings.referral1.payInFee
    const setErrors = (errors: ValidationErrorDTO[]) => {
      state.rowErrors = {}
      state.generalErrors = []

      for (const error of errors) {
        const [, slotKey, field] = (error.field ?? '').split('.')
        if (!slotKey) {
          state.generalErrors.push(error)
          continue
        }

        const row = state.rows.find((item) => referralSlotKey(item.slot) === slotKey)
        if (!row) {
          state.generalErrors.push(error)
          continue
        }

        if (!state.rowErrors[row.slot]) {
          state.rowErrors[row.slot] = []
        }
        state.rowErrors[row.slot].push({...error, field: field ?? 'referralId'})
      }
    }

    const show = (
      rows: ReferralStoreSettingRowFormDTO[],
      errors: ValidationErrorDTO[] = [],
    ) => {
      state.rows = rows.map((row) => ({...row}))
      setErrors(errors ?? [])
      state.modal = true

      asyncFindReferrals('')
      hydrateReferralOverrides()
    }

    const apply = () => {
      emit('apply', state.rows.map((row) => ({...row})))
      state.modal = false
    }

    return {
      ...toRefs(state),
      helper,
      show,
      apply,
      errorsFor,
      asyncFindReferrals,
      onSelectReferral,
      onRemoveReferral,
      toggleReferralFeeType,
    }
  }
})
</script>
